import { Injectable, inject } from '@angular/core';
import { ResultsView } from '@clubhub/ignite/models/affordability';
import {
  IUiProductsSearchModel,
  ProductResultsConfigurationRequestModel,
  ProductResultsConfigurationResponseModel,
  ProductResultsOrder,
  ProductResultsOrderDirection,
  ProductsResultsColumn,
} from '@clubhub/ignite/models/products';
import { IgniteCommonDataService, ProductsSearchService } from '@clubhub/ignite/services';
import { ProductFeeType, roles } from '@shared/constants';
import {
  affordabilityMetFailValue,
  affordabilityMetPassValue,
  affordabilityMetUnknownValue,
  productFilterGroupTitles,
} from '@shared/constants/filter-preference-options';
import { BridgingProductsHelper } from '@shared/helpers/bridging-products.helper';
import { debounceWait } from '@shared/helpers/rxjs-helpers';
import { LendingTypeCode, OutcomeResults, Product } from '@shared/models';
import { DropdownFilterType, ISimpleGridColumn, SimpleGridColumnType, SimpleGridFilterOperator } from '@shared/models/simple-grid';
import { ClubHubDataService } from '@shared/services';
import { AuthorizeService } from '@shared/services/authorize.service';
import { ProductsUtils } from '@shared/utils/products.utils';
import { Subject, of, tap } from 'rxjs';

type ColumnWithEnum = ISimpleGridColumn & { columnEnum: ProductsResultsColumn | null };

@Injectable({
  providedIn: 'root',
})
export class ProductsResultsService {
  private readonly bridgingProductsHelper = inject(BridgingProductsHelper);
  private readonly productsSearchService = inject(ProductsSearchService);
  private readonly productsUtils = inject(ProductsUtils);
  private readonly dataService = inject(ClubHubDataService);
  private readonly authService = inject(AuthorizeService);
  private readonly igniteCommonDataService = inject(IgniteCommonDataService);

  /** Use `next` to set a value on this configuration, and after a debounce period it will be sent to the server. */
  private readonly updateUserResultConfiguration$ = new Subject<[LendingTypeCode, ProductResultsConfigurationRequestModel]>();

  public columnConfigChanged$ = new Subject<LendingTypeCode>();

  public constructor() {
    const productsSearchService = this.productsSearchService;

    if (this.authService.isLoggedIn) {
      // Wait for 5 secs or for the previous request to complete (whichever is longer) before attempting to send another
      debounceWait(this.updateUserResultConfiguration$, 5000, ([lendingTypeCode, request]) =>
        this.dataService.put<never>(`Product/UserResultsConfiguration/${lendingTypeCode}`, request),
      );
    }

    productsSearchService.loadNewResultsConfiguration$?.subscribe(([lendingTypeCode, response]) => {
      this.loadResultsConfigurationResponse(lendingTypeCode, response);
    });
  }

  public readonly trueCostInitialPeriodLabel = 'True cost \n(xx months)';

  public readonly trueCostInitialPeriodHeaderTooltip = 'Products have been sorted in accordance to the FCA cheapest rule';

  private readonly resAndBtlProductsColumns: ColumnWithEnum[] = [
    {
      name: 'lender',
      label: 'Lender',
      columnEnum: ProductsResultsColumn.Lender,
      width: 170,
      headerFilter: 'select',
      sortable: false,
      singularLabel: 'Lender',
      headerStyle: { 'max-width': '166px' },
    },
    {
      name: 'productDescription',
      label: 'Product description',
      columnEnum: ProductsResultsColumn.ProductDescription,
      render: v => v ?? 'Unavailable',
      classList: 'product-no-wrap',
      headerFilter: 'input',
      sortable: false,
    },
    {
      name: 'productCode',
      label: 'Product code',
      columnEnum: ProductsResultsColumn.ProductCode,
      classList: 'product-no-wrap',
      headerFilter: 'input',
      sortable: false,
      headerStyle: { 'max-width': '90px' },
    },
    {
      name: 'type',
      label: 'Type',
      columnEnum: ProductsResultsColumn.ProductType,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      headerTooltip: 'Type',
      showInfoIcon: true,
    },
    {
      name: 'maxLtv',
      label: 'Max LTV',
      columnEnum: ProductsResultsColumn.MaxLtv,
      dropdownFilter: DropdownFilterType.None,
      suffix: '%',
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'initialPeriod',
      label: 'Initial period (months)',
      columnEnum: ProductsResultsColumn.InitialPeriod,
      dropdownFilter: DropdownFilterType.None,
      discreteFilterIcon: 'fa-calendar-alt',
      type: SimpleGridColumnType.Number,
      sortBy: p => (p.forTerm ? Number.MAX_SAFE_INTEGER : p.initialPeriod),
    },
    {
      name: 'initialRateValue',
      label: 'Initial rate',
      columnEnum: ProductsResultsColumn.InitialRate,
      dropdownFilter: DropdownFilterType.None,
      suffix: '%',
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'initialRateType',
      label: 'Rate type',
      columnEnum: ProductsResultsColumn.RateType,
      dropdownFilter: DropdownFilterType.None,
    },
    {
      name: 'revertRate',
      label: 'Revert rate',
      columnEnum: ProductsResultsColumn.RevertRate,
      dropdownFilter: DropdownFilterType.None,
      suffix: '%',
      type: SimpleGridColumnType.Number,
      sortBy: p => this.productsUtils.getRevertRateColumnValue(p as Product) ?? 0,
    },
    {
      name: 'initialMonthlyPayment',
      label: 'Initial monthly payment',
      columnEnum: ProductsResultsColumn.InitialMonthlyPayment,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'monthlyPaymentRevert',
      label: 'Monthly payment revert',
      columnEnum: ProductsResultsColumn.MonthlyPaymentRevert,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'trueCostInitialPeriod',
      label: 'True cost (initial period)',
      columnEnum: ProductsResultsColumn.TrueCostInitialPeriod,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      sortBy: r => r?.trueCostInitialPeriod ?? Infinity,
      headerTooltip: this.trueCostInitialPeriodHeaderTooltip,
    },
    {
      name: 'trueCostCustomPeriod',
      label: this.trueCostInitialPeriodLabel,
      columnEnum: ProductsResultsColumn.TrueCostCustomPeriod,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      sortBy: r => r?.trueCostCustomPeriod ?? Infinity,
      headerStyle: { 'min-width': '80px', 'white-space': 'pre' },
    },
    {
      name: 'trueCost',
      label: 'True cost (term)',
      columnEnum: ProductsResultsColumn.TrueCostTerm,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      sortBy: r => r?.trueCost ?? Infinity,
    },
    {
      name: 'outstandingBalance',
      label: 'Outstanding balance',
      columnEnum: ProductsResultsColumn.OutstandingBalance,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      sortBy: r => r?.outstandingBalance ?? Infinity,
    },
    {
      name: 'earlyRepaymentChargesErcApplied',
      label: 'ERC',
      columnEnum: ProductsResultsColumn.Erc,
      headerStyle: { 'min-width': '40px' },
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
    },
    {
      name: 'cashback',
      label: 'Cashback',
      columnEnum: ProductsResultsColumn.Cashback,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      sortBy: r => r.cashback ?? -1,
    },
    {
      name: 'product_fee_amount_payable',
      label: 'Total fees',
      columnEnum: ProductsResultsColumn.TotalFees,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      render: (_, product) => product
        ? this.productsUtils.calculateTotalFees(product as Product, this.productsSearchService, true)
        : 0,
      sortBy: r => this.productsUtils.calculateTotalFees(r as Product, this.productsSearchService, true),
    },
    {
      name: 'arrangementFee',
      label: 'Arrangement fee',
      columnEnum: ProductsResultsColumn.ArrangementFee,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      sortBy: r => r.fees[ProductFeeType.Arrangement] ?? -1,
    },
    {
      name: 'freeLegal',
      label: 'Free legal',
      columnEnum: ProductsResultsColumn.FreeLegal,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      render: (_, p) => (p as Product).freeLegal || (p as Product).productTransferEligible ? 'Yes' : 'No',
    },
    {
      name: 'overpaymentsAllowed',
      label: 'Overpayments allowed',
      columnEnum: ProductsResultsColumn.OverpaymentsAllowed,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
    },
    {
      name: 'withdrawing',
      label: 'Withdrawing',
      columnEnum: ProductsResultsColumn.Withdrawing,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      headerTooltip: 'This will only show product withdrawal dates where lenders have given permission to show the date in advance',
      showInfoIcon: true,
    },
    {
      name: 'lenderOutcomeResult',
      label: productFilterGroupTitles.criteria,
      columnEnum: ProductsResultsColumn.Criteria,
      textAlignment: 'center',
      sortable: false,
      dropdownFilter: DropdownFilterType.None,
      discreteFilter: [
        {
          label: 'yes',
          value: OutcomeResults.Yes,
          svgIcon: 'checkmark',
          svgClass: 'svg-24 text-colour-green-600',
          svgFill: true,
        },
        {
          label: 'no',
          value: OutcomeResults.No,
          svgIcon: 'close',
          svgClass: 'svg-24 text-colour-red-600',
          svgFill: true,
        },
        {
          label: 'refer',
          value: OutcomeResults.Refer,
        },
      ],
      discreteMultiSelect: true,
    },
    {
      name: 'lenderAffordabilityMet',
      label: productFilterGroupTitles.affordabilityMet,
      columnEnum: ProductsResultsColumn.AffordabilityMet,
      textAlignment: 'center',
      sortable: false,
      dropdownFilter: DropdownFilterType.None,
      discreteFilter: this.getAffordabilityMetFilter(),
      discreteMultiSelect: true,
      headerStyle: { 'max-width': '100px' },
      filterHandler: (value, filter) => {
        if (filter.op === SimpleGridFilterOperator.In && Array.isArray(filter.value)) {
          // For the 'unknown' filter, we ALSO need to match on empty strings, nulls, or undefined.
          if (filter.value.includes(affordabilityMetUnknownValue) && (value === null || value === undefined)) {
            return true;
          }

          return filter.value.includes(value);
        }

        return true;
      },
    },
    {
      name: 'compare',
      label: 'Compare',
      columnEnum: ProductsResultsColumn.Compare,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      textAlignment: 'center',
    },
    {
      name: 'docs',
      label: 'Docs',
      columnEnum: ProductsResultsColumn.Docs,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      hidden: true,
    },
    {
      // This is a fake column that we use that handles the sidebar filters
      name: '_sidebarFilters',
      columnEnum: null,
      hidden: true,
    },
  ];

  private readonly resProductsColumns: ColumnWithEnum[] = [
    ...this.resAndBtlProductsColumns,
    {
      columnEnum: ProductsResultsColumn.MaxAffordableLoan,
      dropdownFilter: DropdownFilterType.None,
      label: 'Max affordable loan',
      name: 'maximumAffordableLoanAmount',
      sortable: true,
      sortBy: r => r?.maximumAffordableLoanAmount ?? -Number.MAX_SAFE_INTEGER,
      type: SimpleGridColumnType.Number,
      headerTooltip: 'Maximum affordable loan based on the best available rate. This figure is not product-specific.',
    },
  ];

  private readonly btlProductsColumns: ColumnWithEnum[] = [
    ...this.resAndBtlProductsColumns,
    {
      columnEnum: ProductsResultsColumn.MaxAffordableLoan,
      dropdownFilter: DropdownFilterType.None,
      label: 'Max affordable loan',
      name: 'maximumAffordableLoanAmount',
      sortable: true,
      sortBy: r => r?.maximumAffordableLoanAmount ?? -Number.MAX_SAFE_INTEGER,
      type: SimpleGridColumnType.Number,
      headerTooltip:
        'All maximum loan sizes are calculated using products currently on offer from each lender. ' +
        'Some of the products used may have significant fees associated with them - please check the product ' +
        'details, or with a lender directly, before making any recommendations to your clients',
    },
    {
      columnEnum: ProductsResultsColumn.RentalRequired,
      dropdownFilter: DropdownFilterType.None,
      headerTooltip: 'Rent required to access this product based on the loan amount requested',
      label: 'Rental required',
      name: 'requiredMonthlyRentalIncome',
      sortable: true,
      sortBy: r => r?.requiredMonthlyRentalIncome ?? -Number.MAX_SAFE_INTEGER,
      type: SimpleGridColumnType.Number,
    },
  ];

  private readonly bridgingProductsColumns: ColumnWithEnum[] = [
    {
      name: 'lender',
      label: 'Lender',
      columnEnum: ProductsResultsColumn.Lender,
      width: 170,
      headerFilter: 'select',
      sortable: false,
      singularLabel: 'Lender',
      headerStyle: { 'max-width': '166px' },
    },
    {
      name: 'description',
      label: 'Product description',
      columnEnum: ProductsResultsColumn.ProductDescription,
      render: v => v ?? 'Unavailable',
      classList: 'product-no-wrap',
      headerFilter: 'input',
      sortable: false,
      headerStyle: { 'max-width': '130px' },
    },
    {
      name: 'code',
      label: 'Product code',
      columnEnum: ProductsResultsColumn.ProductCode,
      headerFilter: 'input',
      sortable: false,
      headerStyle: { 'max-width': '90px' },
    },
    {
      name: 'maxLtv',
      label: 'Max LTV gross',
      columnEnum: ProductsResultsColumn.MaxLtv,
      dropdownFilter: DropdownFilterType.None,
      suffix: '%',
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'maxTerm',
      label: 'Max duration (months)',
      columnEnum: ProductsResultsColumn.MaxDuration,
      suffix: ' months',
      dropdownFilter: DropdownFilterType.None,
      discreteFilter: true,
      discreteFilterIcon: 'fa-calendar-alt',
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'rates',
      label: 'Monthly rate',
      columnEnum: ProductsResultsColumn.MonthlyRate,
      suffix: '%',
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'productType',
      label: 'Rate type',
      columnEnum: ProductsResultsColumn.RateType,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      discreteFilter: this.bridgingProductsHelper.productTypesDiscreteFilter(),
    },
    {
      name: 'monthlyRate',
      label: 'Monthly payment',
      columnEnum: ProductsResultsColumn.InitialMonthlyPayment,
      type: SimpleGridColumnType.Number,
      dropdownFilter: DropdownFilterType.None,
      sortable: true,
    },
    {
      name: 'grossLoan',
      label: 'Gross loan',
      columnEnum: ProductsResultsColumn.GrossLoan,
      dropdownFilter: DropdownFilterType.None,
      sortable: true,
      type: SimpleGridColumnType.Number,
      headerTooltip: 'including all fees added to the loan, charges or interest',
    },
    {
      name: 'grossLtv',
      label: 'Gross LTV',
      columnEnum: ProductsResultsColumn.GrossLtv,
      dropdownFilter: DropdownFilterType.None,
      suffix: '%',
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'trueCost',
      label: 'True cost (term)',
      columnEnum: ProductsResultsColumn.TrueCostTerm,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'exitFee',
      label: 'Exit fee',
      columnEnum: ProductsResultsColumn.ExitFee,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'totalFee',
      label: 'Total fees',
      columnEnum: ProductsResultsColumn.TotalFees,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'aprc',
      label: 'APRC',
      columnEnum: ProductsResultsColumn.Aprc,
      suffix: '%',
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'compare',
      label: 'Compare',
      columnEnum: ProductsResultsColumn.Compare,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      textAlignment: 'center',
    },
    {
      name: 'docs',
      label: 'Docs',
      columnEnum: ProductsResultsColumn.Docs,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      hidden: true,
    },
    {
      // This is a fake column that we use that handles the sidebar filters
      name: '_sidebarFilters',
      columnEnum: null,
      hidden: true,
    },
  ];

  private readonly columnConfigResponses = new Map<string, ProductResultsConfigurationResponseModel>();

  public setTrueCostHeader(
    model: IUiProductsSearchModel,
    productColumns: ISimpleGridColumn[],
    isBridgingType = false,
    forceTitleUpdate = false,
  ): void {
    if (!isBridgingType) {
      if (forceTitleUpdate) {
        const trueCostInitialPeriodColumn = productColumns.find(col => col.name === 'trueCostCustomPeriod');
        if (trueCostInitialPeriodColumn) {
          trueCostInitialPeriodColumn.label = this.trueCostInitialPeriodLabel;
        }
      }
      return;
    }

    const trueCostColumn = productColumns.find(col => col.name === 'trueCost');
    const loanTermMonths = model?.bridging?.loanTermMonths;

    if (trueCostColumn) {
      if (loanTermMonths) {
        const label = loanTermMonths === 1 ? `True cost \n(${loanTermMonths} month)` : `True cost \n(${loanTermMonths} months)`;

        trueCostColumn.label = label;
      } else {
        trueCostColumn.label = 'True cost (term)';
      }
    }
  }

  public getDocsTooltipMessage(isOutcomeResults: boolean, hasTrueCost: boolean, isDirectOnlyProduct: boolean): string {
    if (isOutcomeResults) {
      return 'Please re-run case to enable Documents';
    }

    if (!hasTrueCost) {
      return 'In order to generate Documents, the following information must be entered: Method of Repayment, Mortgage Term.';
    } else if (isDirectOnlyProduct) {
      return 'Documents are unavailable for Direct Only products';
    }

    return '';
  }

  public isDocsButtonEnabled(
    isOutcomeResults: boolean,
    isDirectProduct: boolean,
    isProductHasTrueCost: boolean,
    isAffordabilitySearchIdDefined: boolean,
    caseReran: boolean,
    caseEditedAll: boolean,
  ): boolean {
    if (isOutcomeResults || isDirectProduct || this.igniteCommonDataService.resultsView === ResultsView.ViewAllProducts) {
      return false;
    }

    return isProductHasTrueCost && (!isAffordabilitySearchIdDefined || caseReran || caseEditedAll);
  }

  public getAffordabilityMetFilter() {
    return [
      {
        label: 'yes',
        value: affordabilityMetPassValue,
        svgIcon: 'checkmark',
        svgClass: 'svg-24 text-colour-green-600',
        svgFill: true,
      },
      {
        label: 'no',
        value: affordabilityMetFailValue,
        svgIcon: 'close',
        svgClass: 'svg-24 text-colour-green-600',
        svgFill: true,
      },
      {
        label: 'unknown',
        value: affordabilityMetUnknownValue,
      },
    ];
  }

  public initialiseUserColumnConfiguration(lendingTypeCode: LendingTypeCode) {
    if (!this.authService.isLoggedIn) {
      return of();
    }

    this.productsSearchService.productResultsConfigLoading$.next(true);
    return this.dataService.get<ProductResultsConfigurationResponseModel>(`Product/UserResultsConfiguration/${lendingTypeCode}`).pipe(
      tap(response => {
        this.loadResultsConfigurationResponse(lendingTypeCode, response);
        this.productsSearchService.productResultsConfigLoading$.next(false);
      }),
    );
  }

  /** @param lendingType Name OR code of the lending type. */
  public getColumnsForLendingType(lendingType: string): ColumnWithEnum[] {
    switch (lendingType.toUpperCase()) {
      case LendingTypeCode.Res:
      case 'RESIDENTIAL':
        return this.resProductsColumns;

      case LendingTypeCode.Btl:
      case 'BUYTOLET':
      case 'BUY TO LET':
        return this.btlProductsColumns;

      case LendingTypeCode.Bdg:
      case 'BRIDGING':
        return this.bridgingProductsColumns;

      case LendingTypeCode.LaterLife:
      case LendingTypeCode.SecondCharge.toUpperCase():
        return [];

      default:
        throw new Error(`Unknown lending type code '${lendingType}'.`);
    }
  }

  private getProductsOrder(columns: ColumnWithEnum[]) {
    const order = this.productsSearchService.productsOrder;
    if (!order) {
      return null;
    }

    const column = columns.find(r => r.name === order.sortOn);
    if (!column) {
      return null;
    }

    return {
      sortOn: column.columnEnum,
      sortDir: this.getProductsOrderFromString(order.sortDir),
    } as ProductResultsOrder;
  }

  private getProductsOrderFromString(sortDir: 'asc' | 'desc' | null) {
    switch (sortDir) {
      case 'asc':
        return ProductResultsOrderDirection.Asc;
      case 'desc':
        return ProductResultsOrderDirection.Desc;
      default:
        return null;
    }
  }

  private getProductsOrderDirectionFromEnum(dir: ProductResultsOrderDirection) {
    switch (dir) {
      case ProductResultsOrderDirection.Asc:
        return 'asc';
      case ProductResultsOrderDirection.Desc:
        return 'desc';
    }
  }

  public getSimpleGridProductsSort(lendingTypeCode: LendingTypeCode, productResultsOrder: ProductResultsOrder) {
    const column = this.getColumn(lendingTypeCode, productResultsOrder.sortOn);
    const dir = this.getProductsOrderDirectionFromEnum(productResultsOrder.sortDir) as 'asc' | 'desc';
    if (!column || !dir) {
      return null;
    }

    return {
      sortOn: column.name,
      dir,
    };
  }

  private getColumn(lendingTypeCode: LendingTypeCode, columnEnum: ProductsResultsColumn): ColumnWithEnum {
    const column = this.getColumnsForLendingType(lendingTypeCode).find(c => c.columnEnum === columnEnum);
    if (!column) {
      throw new Error('Could not find column.');
    }
    return column;
  }

  public getColumnConfig(lendingTypeCode: LendingTypeCode, column: ProductsResultsColumn) {
    return this.columnConfigResponses.get(lendingTypeCode)?.columns.find(c => c.column === column);
  }

  public async setColumnVisible(lendingTypeCode: LendingTypeCode, columnEnum: ProductsResultsColumn, visible: boolean) {
    const column = this.getColumn(lendingTypeCode, columnEnum);
    column.hidden = !visible;

    this.updateUserResultConfiguration$.next([
      lendingTypeCode,
      this.getResultConfigurationRequestModel(this.getColumnsForLendingType(lendingTypeCode)),
    ]);

    this.columnConfigChanged$.next(lendingTypeCode);
  }

  public async setColumnOrder(lendingTypeCode: LendingTypeCode, orderedColumnNames: ProductsResultsColumn[]) {
    const columns = this.getColumnsForLendingType(lendingTypeCode);
    columns.sort((a, b) => {
      const aIdx = a.columnEnum !== null ? orderedColumnNames.indexOf(a.columnEnum) : Infinity;
      const bIdx = b.columnEnum !== null ? orderedColumnNames.indexOf(b.columnEnum) : Infinity;
      return aIdx - bIdx;
    });

    this.updateUserResultConfiguration$.next([lendingTypeCode, this.getResultConfigurationRequestModel(columns)]);
    this.columnConfigChanged$.next(lendingTypeCode);
  }

  private getResultConfigurationRequestModel(
    columns: ColumnWithEnum[],
    saveProductsOrder = false,
  ): ProductResultsConfigurationRequestModel {
    const productResultsOrder = saveProductsOrder ? this.getProductsOrder(columns) : null;
    return {
      columns: columns
        .filter(c => c.columnEnum !== null)
        .map(c => ({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          column: c.columnEnum!,
          isVisible: !c.hidden,
        })),
      productResultsOrder: productResultsOrder,
    };
  }

  public getCurrentResultConfigurationRequestModel(
    lendingTypeCode: LendingTypeCode,
    saveProductsOrder = false,
  ): ProductResultsConfigurationRequestModel {
    const columns = this.getColumnsForLendingType(lendingTypeCode);
    return this.getResultConfigurationRequestModel(columns, saveProductsOrder);
  }

  public get canDisplayEor(): boolean {
    return this.authService.hasRole(roles.clubHubGenerateEOR);
  }

  public canDisplayEsis(lendingTypeCode: LendingTypeCode): boolean {
    return (
      (this.authService.hasRole(roles.clubHubGenerateEsisResidential) && lendingTypeCode === LendingTypeCode.Res) ||
      (this.authService.hasRole(roles.clubHubGenerateEsisBuyToLet) && lendingTypeCode === LendingTypeCode.Btl)
    );
  }

  public canDisplayDocs(lendingTypeCode: LendingTypeCode): boolean {
    // LendingTypeCode.Bdg is incorrectly cased
    if ([LendingTypeCode.Bdg, 'Bdg', LendingTypeCode.LaterLife, LendingTypeCode.SecondCharge].includes(lendingTypeCode)) {
      return false;
    }

    const hasEsisRole = this.canDisplayEsis(lendingTypeCode);
    const hasEorRole = this.canDisplayEor;

    return hasEsisRole || hasEorRole;
  }

  public isColumnNotRestricted(columnEnum: ProductsResultsColumn, lendingTypeCode: LendingTypeCode) {
    if (columnEnum === ProductsResultsColumn.Docs) {
      return this.canDisplayDocs(lendingTypeCode);
    }
    return true;
  }

  private loadResultsConfigurationResponse(lendingTypeCode: LendingTypeCode, response: ProductResultsConfigurationResponseModel) {
    this.columnConfigResponses.set(lendingTypeCode, response);

    const columns = this.getColumnsForLendingType(lendingTypeCode);
    columns.sort((a, b) => {
      const aIdx = response.columns.findIndex(c => c.column === a.columnEnum);
      const bIdx = response.columns.findIndex(c => c.column === b.columnEnum);
      return aIdx - bIdx;
    });

    for (const column of columns) {
      if (column.columnEnum) {
        const isColumnAllowToBeVisible = this.isColumnNotRestricted(column.columnEnum, lendingTypeCode);
        if (isColumnAllowToBeVisible === false) {
          column.hidden = true;
        } else {
          const columnConfig = response.columns.find(c => c.column === column.columnEnum);
          column.hidden = !columnConfig?.isVisible;
        }
      }
    }
  }
}
